/* eslint-disable camelcase */
import React, { forwardRef, useImperativeHandle, useEffect, useState } from "react"
import { Grid, TextField, Box, Paper, Typography, MenuItem, InputAdornment, CircularProgress } from "@mui/material"
import { Person, Flag, FormatListNumbered } from "@mui/icons-material"
import { useFormik } from "formik"
import * as Yup from "yup"
import { api } from "../../../../api"

const FormularioCandidato = forwardRef(({ onSubmit, isLoading, modo, initialData }, ref) => {
  const [dignidades, setDignidades] = useState([])
  const [loadingDignidades, setLoadingDignidades] = useState(true)

  // ========================================================
  // 1. CARGA DINÁMICA DEL CATÁLOGO DE DIGNIDADES
  // ========================================================
  useEffect(() => {
    const fetchDignidades = async () => {
      try {
        const res = await api.post("/ControlElectoral/getDignidadesDropdown", {})
        if (res.data?.success) {
          setDignidades(res.data.data)
        }
      } catch (error) {
        console.error("Error al cargar el catálogo de dignidades", error)
      } finally {
        setLoadingDignidades(false)
      }
    }
    fetchDignidades()
  }, [])

  // ========================================================
  // 2. CONFIGURACIÓN DE VALIDACIONES (YUP) Y ESTADO (FORMIK)
  // ========================================================
  const formik = useFormik({
    initialValues: {
      cand_codigo: "",
      dig_codigo: "", // Reemplaza cand_dignidad
      cand_nombre: "",
      cand_partido: "",
      cand_lista: "",
      cand_orden: "",
      cand_estado: "A",
    },
    validationSchema: Yup.object({
      cand_codigo: Yup.string().required("El código de candidato es obligatorio").max(10, "Máximo 10 caracteres"),
      dig_codigo: Yup.number().required("Debe seleccionar una dignidad"),
      cand_nombre: Yup.string().required("El nombre del candidato es obligatorio").max(150, "Máximo 150 caracteres"),
      cand_partido: Yup.string().required("El partido político es obligatorio").max(100, "Máximo 100 caracteres"),
      cand_lista: Yup.string().required("El número/nombre de lista es obligatorio").max(10, "Máximo 10 caracteres"),
      cand_orden: Yup.number()
        .typeError("Debe ser un número válido")
        .required("El orden en la papeleta es obligatorio")
        .positive("Debe ser mayor a 0")
        .integer("Debe ser un número entero"),
    }),
    onSubmit: async (values) => {
      if (modo === "editar") {
        const payloadEdit = {
          cand_codigoOld: initialData.cand_codigo,
          dig_codigoNew: values.dig_codigo,
          cand_nombreNew: values.cand_nombre,
          cand_partidoNew: values.cand_partido,
          cand_listaNew: values.cand_lista,
          cand_ordenNew: values.cand_orden,
          cand_estado: values.cand_estado,
        }
        await onSubmit(payloadEdit)
      } else {
        await onSubmit(values)
      }
    },
  })

  // ========================================================
  // 3. EXPOSICIÓN DEL MÉTODO SUBMIT AL PADRE (TOP TOOLBAR)
  // ========================================================
  useImperativeHandle(ref, () => ({
    handleSubmit: formik.submitForm,
  }))

  // ========================================================
  // 4. CARGA DE DATOS INICIALES (SOLO MODO EDICIÓN)
  // ========================================================
  useEffect(() => {
    if (modo === "editar" && initialData) {
      formik.setValues({
        cand_codigo: initialData.cand_codigo || "",
        dig_codigo: initialData.dig_codigo || "",
        cand_nombre: initialData.cand_nombre || "",
        cand_partido: initialData.cand_partido || "",
        cand_lista: initialData.cand_lista || "",
        cand_orden: initialData.cand_orden || "",
        cand_estado: initialData.cand_estado || "A",
      })
    }
  }, [modo, initialData])

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container spacing={3}>
        {/* SECCIÓN 1: DATOS PERSONALES Y DIGNIDAD */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%" }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <Person sx={{ mr: 1 }} /> Identificación del Candidato
            </Typography>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={5}>
                <TextField
                  fullWidth
                  id="cand_codigo"
                  name="cand_codigo"
                  label="Código Único *"
                  disabled={modo === "editar" || isLoading}
                  value={formik.values.cand_codigo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.cand_codigo && Boolean(formik.errors.cand_codigo)}
                  helperText={formik.touched.cand_codigo && formik.errors.cand_codigo}
                  inputProps={{ maxLength: 10, style: { textTransform: "uppercase", fontWeight: "bold" } }}
                />
              </Grid>

              <Grid item xs={12} sm={7}>
                <TextField
                  fullWidth
                  select
                  id="dig_codigo"
                  name="dig_codigo"
                  label="Dignidad a Postular *"
                  disabled={isLoading || loadingDignidades}
                  value={formik.values.dig_codigo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.dig_codigo && Boolean(formik.errors.dig_codigo)}
                  helperText={formik.touched.dig_codigo && formik.errors.dig_codigo}
                >
                  {loadingDignidades ? (
                    <MenuItem disabled value="">
                      <CircularProgress size={20} sx={{ mr: 1 }} /> Cargando...
                    </MenuItem>
                  ) : dignidades.length === 0 ? (
                    <MenuItem disabled value="">
                      Sin dignidades registradas
                    </MenuItem>
                  ) : (
                    dignidades.map((dig) => (
                      <MenuItem key={dig.id} value={dig.id}>
                        {dig.label}
                      </MenuItem>
                    ))
                  )}
                </TextField>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="cand_nombre"
                  name="cand_nombre"
                  label="Nombre Completo en Papeleta *"
                  disabled={isLoading}
                  value={formik.values.cand_nombre}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.cand_nombre && Boolean(formik.errors.cand_nombre)}
                  helperText={formik.touched.cand_nombre && formik.errors.cand_nombre}
                  inputProps={{ maxLength: 150 }}
                />
              </Grid>

              {modo === "editar" && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    select
                    id="cand_estado"
                    name="cand_estado"
                    label="Estado de Participación"
                    disabled={isLoading}
                    value={formik.values.cand_estado}
                    onChange={formik.handleChange}
                  >
                    <MenuItem value="A">ACTIVO (En contienda)</MenuItem>
                    <MenuItem value="I">INACTIVO (Descalificado / Retirado)</MenuItem>
                  </TextField>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* SECCIÓN 2: DATOS POLÍTICOS Y PAPELETA */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, borderRadius: 2, height: "100%", bgcolor: "#f9fbfd" }}>
            <Typography variant="h6" color="primary" fontWeight="bold" mb={3} display="flex" alignItems="center">
              <Flag sx={{ mr: 1 }} /> Información Política
            </Typography>

            <Box mb={2}>
              <Typography variant="caption" color="textSecondary" display="block" mb={2}>
                Especifique los datos de la organización política y la ubicación exacta del candidato en la papeleta del
                CNE.
              </Typography>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="cand_partido"
                    name="cand_partido"
                    label="Partido / Movimiento Político *"
                    disabled={isLoading}
                    value={formik.values.cand_partido}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cand_partido && Boolean(formik.errors.cand_partido)}
                    helperText={formik.touched.cand_partido && formik.errors.cand_partido}
                    inputProps={{ maxLength: 100 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="cand_lista"
                    name="cand_lista"
                    label="Número de Lista *"
                    disabled={isLoading}
                    value={formik.values.cand_lista}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cand_lista && Boolean(formik.errors.cand_lista)}
                    helperText={formik.touched.cand_lista && formik.errors.cand_lista}
                    inputProps={{ maxLength: 10 }}
                  />
                </Grid>

                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    type="number"
                    id="cand_orden"
                    name="cand_orden"
                    label="Orden en Papeleta *"
                    disabled={isLoading}
                    value={formik.values.cand_orden}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.cand_orden && Boolean(formik.errors.cand_orden)}
                    helperText={formik.touched.cand_orden && formik.errors.cand_orden}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <FormatListNumbered fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </form>
  )
})

export default FormularioCandidato
